import { Eye, Film, Heart, Layers, UserPlus, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { StatTile } from '@/components/marketing/MetricTile';
import { RankedList } from '@/components/marketing/RankedList';
import { TrendChart } from '@/components/marketing/TrendChart';
import {
  ChannelStatusNotice,
  resolveChannelStatus,
} from '@/components/marketing/ChannelStatusNotice';
import { comparisonLabel } from '@/components/marketing/RangeSelector';
import { CHART_SERIES } from '@/lib/brand';
import { formatDate } from '@/lib/formatters';
import { PLATFORM_ICONS, PLATFORM_LABELS } from '@/features/social/constants';
import {
  SOCIAL_PLATFORMS,
  useSocialAnalytics,
  type SocialPlatform,
  type SocialPlatformData,
  type SocialPostRow,
} from '@/features/marketing/hooks';
import {
  formatCompactNumber,
  formatDuration,
  formatRatioPercent,
  pctChange,
  type MarketingRange,
} from '@/features/marketing/range';

interface SocialTabProps {
  range: MarketingRange;
  /** Selected platform section, URL-synced by the parent page. */
  platform: SocialPlatform | 'all';
  onPlatformChange: (platform: SocialPlatform | 'all') => void;
}

/**
 * Organic social performance, split into a cross-platform roll-up plus one
 * section per network.
 *
 * The four networks report fundamentally different metrics — YouTube has no
 * reach or impressions, TikTok has no reach, only Instagram reports saves —
 * so rather than forcing a lowest-common-denominator table, each section
 * renders the metrics its platform actually supports and hides the rest.
 * Migration 101 is the reference for what each platform populates.
 */
export function SocialTab({ range, platform, onPlatformChange }: SocialTabProps) {
  const query = useSocialAnalytics(range);
  const data = query.data;

  const status = resolveChannelStatus({
    isLoading: query.isLoading,
    error: query.error,
    connected: data?.connected,
    awaitingFirstSync: data?.platforms.some((p) => p.awaitingFirstSync),
  });

  return (
    <div className="space-y-6">
      <ChannelStatusNotice
        status={status}
        channel="Social accounts"
        error={query.error instanceof Error ? query.error.message : null}
      />

      <Tabs
        value={platform}
        onValueChange={(next) => onPlatformChange(next as SocialPlatform | 'all')}
      >
        <TabsList className="h-auto flex-wrap gap-1">
          <TabsTrigger value="all">
            <Layers className="mr-1.5 h-4 w-4" />
            All platforms
          </TabsTrigger>
          {SOCIAL_PLATFORMS.map((id) => {
            const Icon = PLATFORM_ICONS[id];
            const section = data?.platforms.find((p) => p.platform === id);
            return (
              <TabsTrigger key={id} value={id}>
                <Icon className="mr-1.5 h-4 w-4" />
                {PLATFORM_LABELS[id]}
                {section && !section.connected && (
                  <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <AllPlatformsSection range={range} data={data} loading={query.isLoading} />
        </TabsContent>

        {SOCIAL_PLATFORMS.map((id) => (
          <TabsContent key={id} value={id} className="mt-6">
            <PlatformSection
              range={range}
              platform={id}
              data={data?.platforms.find((p) => p.platform === id)}
              loading={query.isLoading}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

// ─── Cross-platform roll-up ────────────────────────────────────────────

function AllPlatformsSection({
  range,
  data,
  loading,
}: {
  range: MarketingRange;
  data: ReturnType<typeof useSocialAnalytics>['data'];
  loading: boolean;
}) {
  const totals = data?.totals;
  const previous = data?.previous;
  const deltaLabel = comparisonLabel(range);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Total followers"
          icon={Users}
          loading={loading}
          value={formatCompactNumber(totals?.followers ?? 0)}
          delta={pctChange(totals?.followerGrowth ?? 0, previous?.followerGrowth ?? 0)}
          deltaLabel={deltaLabel}
          hint={
            totals
              ? `${signed(totals.followerGrowth)} this period`
              : undefined
          }
        />
        <StatTile
          label="Engagements"
          icon={Heart}
          loading={loading}
          value={formatCompactNumber(totals?.engagements ?? 0)}
          delta={pctChange(totals?.engagements ?? 0, previous?.engagements ?? 0)}
          deltaLabel={deltaLabel}
          hint={totals ? `${formatRatioPercent(totals.engagementRate, 2)} rate` : undefined}
        />
        <StatTile
          label="Reach"
          icon={Eye}
          loading={loading}
          value={formatCompactNumber(totals?.reach ?? 0)}
          delta={pctChange(totals?.reach ?? 0, previous?.reach ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Video views"
          icon={Film}
          loading={loading}
          value={formatCompactNumber(totals?.videoViews ?? 0)}
          delta={pctChange(totals?.videoViews ?? 0, previous?.videoViews ?? 0)}
          deltaLabel={deltaLabel}
          hint={
            totals?.watchTimeSeconds
              ? `${formatDuration(totals.watchTimeSeconds)} watched`
              : undefined
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Audience and engagement</CardTitle>
        </CardHeader>
        <CardContent>
          <TrendChart
            data={data?.daily ?? []}
            height={280}
            formatLeftAxis={formatCompactNumber}
            formatRightAxis={formatCompactNumber}
            series={[
              {
                key: 'engagements',
                label: 'Engagements',
                color: CHART_SERIES[2],
                kind: 'bar',
                axis: 'right',
              },
              { key: 'followers', label: 'Followers', color: CHART_SERIES[0], kind: 'area' },
            ]}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Platform comparison</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-border">
            {(data?.platforms ?? []).map((section) => {
              const Icon = PLATFORM_ICONS[section.platform];
              return (
                <div
                  key={section.platform}
                  className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.8} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {PLATFORM_LABELS[section.platform]}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {section.connected
                        ? `${formatCompactNumber(section.totals.engagements)} engagements · ${section.totals.postsPublished} posts`
                        : section.awaitingFirstSync
                          ? 'Connected — first sync pending'
                          : 'Not connected'}
                    </p>
                  </div>
                  {section.connected ? (
                    <div className="shrink-0 text-right">
                      <p className="tabular-nums text-sm font-medium text-foreground">
                        {formatCompactNumber(section.totals.followers)}
                      </p>
                      <p className="text-[11px] tabular-nums text-muted-foreground">
                        {signed(section.totals.followerGrowth)}
                      </p>
                    </div>
                  ) : (
                    <Badge variant="muted" className="shrink-0">
                      —
                    </Badge>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <TopPostsCard
          posts={data?.topPosts ?? []}
          title="Top posts across platforms"
          showPlatform
        />
      </div>
    </div>
  );
}

// ─── Single-platform section ───────────────────────────────────────────

function PlatformSection({
  range,
  platform,
  data,
  loading,
}: {
  range: MarketingRange;
  platform: SocialPlatform;
  data: SocialPlatformData | undefined;
  loading: boolean;
}) {
  const label = PLATFORM_LABELS[platform];
  const totals = data?.totals;
  const previous = data?.previous;
  const deltaLabel = comparisonLabel(range);

  const status = resolveChannelStatus({
    isLoading: loading,
    error: null,
    connected: data?.connected,
    awaitingFirstSync: data?.awaitingFirstSync,
  });

  if (status === 'not-connected') {
    return (
      <ChannelStatusNotice
        status="not-connected"
        channel={label}
      />
    );
  }

  // Each platform reports a different subset — see migration 101. Rendering a
  // hard zero for a metric the platform never reports would read as a
  // collapse in performance, so unsupported tiles are omitted entirely.
  const supports = PLATFORM_METRIC_SUPPORT[platform];

  return (
    <div className="space-y-6">
      <ChannelStatusNotice status={status} channel={label} />

      {data && data.accounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {data.accounts.map((account) => (
            <Badge key={account.id} variant="secondary">
              {account.handle ? `@${account.handle}` : account.name}
            </Badge>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Followers"
          icon={Users}
          loading={loading}
          value={formatCompactNumber(totals?.followers ?? 0)}
          delta={pctChange(totals?.followerGrowth ?? 0, previous?.followerGrowth ?? 0)}
          deltaLabel={deltaLabel}
          hint={totals ? `${signed(totals.followerGrowth)} this period` : undefined}
        />
        <StatTile
          label="Engagements"
          icon={Heart}
          loading={loading}
          value={formatCompactNumber(totals?.engagements ?? 0)}
          delta={pctChange(totals?.engagements ?? 0, previous?.engagements ?? 0)}
          deltaLabel={deltaLabel}
          hint={totals ? `${formatRatioPercent(totals.engagementRate, 2)} rate` : undefined}
        />
        {supports.reach ? (
          <StatTile
            label="Reach"
            icon={Eye}
            loading={loading}
            value={formatCompactNumber(totals?.reach ?? 0)}
            delta={pctChange(totals?.reach ?? 0, previous?.reach ?? 0)}
            deltaLabel={deltaLabel}
          />
        ) : (
          <StatTile
            label="Video views"
            icon={Film}
            loading={loading}
            value={formatCompactNumber(totals?.videoViews ?? 0)}
            delta={pctChange(totals?.videoViews ?? 0, previous?.videoViews ?? 0)}
            deltaLabel={deltaLabel}
            hint={
              totals?.watchTimeSeconds
                ? `${formatDuration(totals.watchTimeSeconds)} watched`
                : undefined
            }
          />
        )}
        <StatTile
          label={supports.profileViews ? 'Profile views' : 'Posts published'}
          icon={supports.profileViews ? UserPlus : Film}
          loading={loading}
          value={
            supports.profileViews
              ? formatCompactNumber(totals?.profileViews ?? 0)
              : (totals?.postsPublished ?? 0).toLocaleString()
          }
          delta={
            supports.profileViews
              ? pctChange(totals?.profileViews ?? 0, previous?.profileViews ?? 0)
              : pctChange(totals?.postsPublished ?? 0, previous?.postsPublished ?? 0)
          }
          deltaLabel={deltaLabel}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{label} over time</CardTitle>
        </CardHeader>
        <CardContent>
          <TrendChart
            data={data?.daily ?? []}
            height={280}
            formatLeftAxis={formatCompactNumber}
            formatRightAxis={formatCompactNumber}
            series={[
              {
                key: 'engagements',
                label: 'Engagements',
                color: CHART_SERIES[2],
                kind: 'bar',
                axis: 'right',
              },
              { key: 'followers', label: 'Followers', color: CHART_SERIES[0], kind: 'area' },
              ...(supports.reach
                ? [
                    {
                      key: 'reach',
                      label: 'Reach',
                      color: CHART_SERIES[3],
                      kind: 'line' as const,
                      axis: 'right' as const,
                    },
                  ]
                : [
                    {
                      key: 'videoViews',
                      label: 'Video views',
                      color: CHART_SERIES[3],
                      kind: 'line' as const,
                      axis: 'right' as const,
                    },
                  ]),
            ]}
          />
        </CardContent>
      </Card>

      <TopPostsCard
        posts={data?.topPosts ?? []}
        title={`Top ${label} posts`}
      />
    </div>
  );
}

/**
 * Which metrics each network actually reports. Drives whether a tile renders
 * at all, so an unsupported metric never shows up as a zero.
 */
const PLATFORM_METRIC_SUPPORT: Record<
  SocialPlatform,
  { reach: boolean; impressions: boolean; profileViews: boolean; saves: boolean }
> = {
  facebook: { reach: true, impressions: true, profileViews: true, saves: false },
  instagram: { reach: true, impressions: true, profileViews: true, saves: true },
  tiktok: { reach: false, impressions: false, profileViews: true, saves: false },
  youtube: { reach: false, impressions: false, profileViews: false, saves: false },
};

// ─── Top posts ─────────────────────────────────────────────────────────

function TopPostsCard({
  posts,
  title,
  showPlatform,
}: {
  posts: SocialPostRow[];
  title: string;
  showPlatform?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <RankedList
          rows={posts.slice(0, 8).map((post) => ({
            key: post.id,
            label: postLabel(post),
            href: post.url ?? undefined,
            value: post.engagements,
            hint: [
              showPlatform ? PLATFORM_LABELS[post.platform] : null,
              post.postedAt ? formatDate(post.postedAt) : null,
              describeReachOrViews(post),
            ]
              .filter(Boolean)
              .join(' · '),
            meta: `${formatCompactNumber(post.likes)} likes · ${formatCompactNumber(post.comments)} comments`,
          }))}
          formatValue={formatCompactNumber}
          emptyLabel="No posts with engagement in this period."
        />
      </CardContent>
    </Card>
  );
}

/** Falls back through title → URL → id so a row is never blank. */
function postLabel(post: SocialPostRow): string {
  const title = post.title?.trim();
  if (title) return title.length > 90 ? `${title.slice(0, 90)}…` : title;
  if (post.url) return post.url;
  return post.id.split(':')[1] ?? post.id;
}

function describeReachOrViews(post: SocialPostRow): string {
  if (post.reach > 0) return `${formatCompactNumber(post.reach)} reach`;
  if (post.impressions > 0) return `${formatCompactNumber(post.impressions)} impressions`;
  if (post.videoViews > 0) return `${formatCompactNumber(post.videoViews)} views`;
  return '';
}

function signed(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toLocaleString()}`;
}
